'use client';

import Image from '@/components/ui/CspImage';
import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react';
import { Link, usePathname } from '@/i18n/navigation';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { DOCS_BASE, DOCS_NAV, docHref, prevNext } from './docsNav';
import SiteFooter from '@/components/landing/SiteFooter';

export default function DocsShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const slug = pathname.replace(`${DOCS_BASE}/`, '').replace(DOCS_BASE, '') || 'introduction';
  const { prev, next } = prevNext(slug);

  return (
    <div className="landing-theme min-h-screen bg-background font-body">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link href="/" className="group flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
            <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-1" />
            <span className="text-sm font-medium">Back to StyTrix</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link href="/" className="flex items-center">
              <Image src="/StyTrix_brand.png" alt="StyTrix" width={120} height={32}  className="block dark:hidden w-auto h-[2rem]" priority />
              <Image src="/StyTrix_brand_dark.png" alt="StyTrix" width={120} height={32}  className="hidden dark:block w-auto h-[2rem]" priority />
            </Link>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl gap-10 px-6">
        {/* Sidebar */}
        <aside className="hidden md:block w-60 shrink-0 border-r border-border py-10 pr-6">
          <nav aria-label="Documentation" className="sticky top-24 space-y-7">
            <div className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Documentation</div>
            {DOCS_NAV.map((group) => (
              <div key={group.group}>
                <div className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{group.group}</div>
                <ul className="space-y-0.5">
                  {group.items.map((item) => {
                    const href = docHref(item.slug);
                    const active = pathname === href;
                    return (
                      <li key={item.slug}>
                        <Link
                          href={href}
                          className={`block rounded-lg px-3 py-1.5 text-sm transition-colors ${
                            active
                              ? 'bg-primary/10 font-medium text-primary'
                              : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                          }`}
                        >
                          {item.title}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
            <div className="px-3 pt-4 border-t border-border">
              <a href="mailto:hello@stytrix.com" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors">
                Need help? <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </nav>
        </aside>

        {/* Content */}
        <main id="main-content" className="min-w-0 flex-1 py-10 md:py-12">
          <article className="max-w-3xl">{children}</article>

          {/* Prev / Next */}
          <div className="mt-14 grid max-w-3xl grid-cols-2 gap-4 border-t border-border pt-6">
            <div>
              {prev && (
                <Link href={docHref(prev.slug)} className="group flex flex-col rounded-xl border border-border p-4 hover:border-primary/40 transition-colors">
                  <span className="text-xs text-muted-foreground">Previous</span>
                  <span className="mt-1 flex items-center gap-1 font-medium text-foreground group-hover:text-primary">
                    <ArrowLeft className="h-4 w-4" /> {prev.title}
                  </span>
                </Link>
              )}
            </div>
            <div>
              {next && (
                <Link href={docHref(next.slug)} className="group flex flex-col items-end rounded-xl border border-border p-4 text-right hover:border-primary/40 transition-colors">
                  <span className="text-xs text-muted-foreground">Next</span>
                  <span className="mt-1 flex items-center gap-1 font-medium text-foreground group-hover:text-primary">
                    {next.title} <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              )}
            </div>
          </div>
        </main>
      </div>
      <SiteFooter />
    </div>
  );
}
